import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { TimesheetEntry } from '@gigdriver/domain';
import {
  clockIn as clockInApi,
  clockOut as clockOutApi,
  createTimesheetEntry as createEntryApi,
  updateTimesheetEntry as updateEntryApi,
  reviewTimesheetEntry as reviewEntryApi,
  reopenTimesheetEntry as reopenEntryApi,
  deleteTimesheetEntry as deleteEntryApi,
  watchGigTimesheetEntries,
  watchMyTimesheetEntries,
  watchReviewQueue,
} from '@gigdriver/client';

// Three independent live views over timesheet entries — the signed-in
// person's own (Time Clock page), one gig's (Gig Detail → Timesheets,
// coordinator+), and the org-wide review queue (coordinator+) — plus the
// callable actions. Writes never touch Firestore directly: the rules deny
// them, so every action goes through a Cloud Function.
export const useTimesheetsStore = defineStore('timesheets', () => {
  const myEntries = ref<TimesheetEntry[]>([]);
  const gigEntries = ref<TimesheetEntry[]>([]);
  const queueEntries = ref<TimesheetEntry[]>([]);
  const busy = ref(false);
  const error = ref<string | null>(null);

  const myOpenEntry = computed(() => myEntries.value.find(e => e.status === 'OPEN') ?? null);

  let stopMine: (() => void) | null = null;
  let stopGig: (() => void) | null = null;
  let stopQueue: (() => void) | null = null;

  const fail = (ctx: string) => (err: unknown) => {
    error.value = err instanceof Error ? err.message : ctx;
  };

  function watchMine(userId: string) {
    stopMine?.();
    myEntries.value = [];
    stopMine = watchMyTimesheetEntries(userId, { onData: e => (myEntries.value = e), onError: fail('Failed to load your time') });
  }

  function watchGig(orgId: string, gigId: string) {
    stopGig?.();
    gigEntries.value = [];
    stopGig = watchGigTimesheetEntries(orgId, gigId, {
      onData: e => (gigEntries.value = e),
      onError: fail('Failed to load timesheets'),
    });
  }

  function watchQueue(orgId: string) {
    stopQueue?.();
    queueEntries.value = [];
    stopQueue = watchReviewQueue(orgId, { onData: e => (queueEntries.value = e), onError: fail('Failed to load review queue') });
  }

  // Each page stops ONLY the watcher it started. A shared "stop everything"
  // is a trap: when navigating between pages the incoming page mounts before
  // the outgoing one unmounts, so the old page's cleanup would kill the new
  // page's live subscription.
  function stopMineWatch() {
    stopMine?.();
    stopMine = null;
  }
  function stopGigWatch() {
    stopGig?.();
    stopGig = null;
  }
  function stopQueueWatch() {
    stopQueue?.();
    stopQueue = null;
  }

  // Runs a write, tracking busy/error; rethrows so the caller can react.
  async function run<T>(fn: () => Promise<T>, ctx: string): Promise<T> {
    busy.value = true;
    error.value = null;
    try {
      return await fn();
    } catch (err) {
      fail(ctx)(err);
      throw err;
    } finally {
      busy.value = false;
    }
  }

  return {
    myEntries,
    gigEntries,
    queueEntries,
    myOpenEntry,
    busy,
    error,
    watchMine,
    watchGig,
    watchQueue,
    stopMineWatch,
    stopGigWatch,
    stopQueueWatch,
    clockIn: (input: Parameters<typeof clockInApi>[0]) => run(() => clockInApi(input), 'Failed to clock in'),
    clockOut: (input: Parameters<typeof clockOutApi>[0]) => run(() => clockOutApi(input), 'Failed to clock out'),
    createEntry: (input: Parameters<typeof createEntryApi>[0]) => run(() => createEntryApi(input), 'Failed to add time'),
    updateEntry: (input: Parameters<typeof updateEntryApi>[0]) => run(() => updateEntryApi(input), 'Failed to update entry'),
    reviewEntry: (input: Parameters<typeof reviewEntryApi>[0]) => run(() => reviewEntryApi(input), 'Failed to review entry'),
    reopenEntry: (input: Parameters<typeof reopenEntryApi>[0]) => run(() => reopenEntryApi(input), 'Failed to reopen entry'),
    deleteEntry: (input: Parameters<typeof deleteEntryApi>[0]) => run(() => deleteEntryApi(input), 'Failed to delete entry'),
  };
});
